/**
 * PasswordResetPage — public forgot-password request page.
 *
 * SCRUM-57 follow-up to Codex review #1: the LoginPage's "Forgot
 * password?" affordance previously linked to `/password-reset` but no
 * route was declared, so the link landed on the 404 catch-all. This
 * page closes that gap with the smallest functional surface that
 * matches the backend contract:
 *
 *   POST /auth/forgot-password (Public + CSRF) — body { email }.
 *   Response is OWASP-aligned: always 200 with a constant body
 *   regardless of whether the email exists. The UI mirrors that —
 *   any 2xx renders the constant success message.
 *
 * The follow-on `/auth/reset-password` (token-bearing) flow is a
 * separate ticket (not in scope here).
 *
 * Tokens-only colors via `@sparta/design-tokens` Tailwind preset and
 * the semantic CSS variables declared in `apps/admin/src/index.css`
 * (`--bg-canvas`, `--fg-primary`, …). Touch targets ≥ 44 px,
 * `aria-live` status region, semantic landmarks per WCAG 2.1 AA
 * (CLAUDE.md Frontend rule §7).
 */

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  requestPasswordResetSchema,
  type RequestPasswordResetInput,
} from '@artemis/validators';
import { Button, Card, TextField } from '@sparta/ui-kit';
import { apiClient } from '../../shared/api-client.js';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const MAX_EMAIL_LENGTH = 254;

export function PasswordResetPage(): React.ReactElement {
  const [submitState, setSubmitState] = React.useState<SubmitState>({
    kind: 'idle',
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RequestPasswordResetInput>({
    resolver: zodResolver(requestPasswordResetSchema),
    mode: 'onTouched',
  });

  const onSubmit = handleSubmit(async (values): Promise<void> => {
    // Form-level email length cap layered on top of the SSOT schema —
    // same pattern as LoginPage. Zod's regex does not enforce the RFC
    // 5321 254-char total length.
    if (values.email.length > MAX_EMAIL_LENGTH) {
      setError('email', { message: 'Invalid email — too long.' });
      return;
    }

    setSubmitState({ kind: 'submitting' });
    try {
      const { response } = await apiClient.POST('/auth/forgot-password', {
        body: { email: values.email },
      });
      if (response.ok) {
        setSubmitState({ kind: 'success' });
        return;
      }
      if (response.status === 429) {
        setSubmitState({
          kind: 'error',
          message: 'Too many attempts. Please wait a moment and try again.',
        });
      } else if (response.status >= 500) {
        setSubmitState({
          kind: 'error',
          message:
            'Password reset is temporarily unavailable. Try again shortly.',
        });
      } else {
        setSubmitState({
          kind: 'error',
          message: 'Something went wrong. Try again.',
        });
      }
    } catch {
      setSubmitState({
        kind: 'error',
        message: 'Network error. Check your connection and try again.',
      });
    }
  });

  const submitting = submitState.kind === 'submitting';

  return (
    <main
      className="min-h-screen w-full"
      style={{ background: 'var(--bg-canvas)', color: 'var(--fg-primary)' }}
    >
      <div className="mx-auto max-w-md px-4 py-12 sm:py-16">
        <h1
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px' }}
        >
          Reset your password
        </h1>
        <p
          className="mb-8 mt-2 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Enter the email associated with your account and we'll send you a
          link to reset your password.
        </p>

        {submitState.kind === 'success' ? (
          // SCRUM-99: success surface composes the kit Card primitive
          // (variant="surface", padding="md") via asChild so the
          // semantic <section> + role + aria-live stay at this call
          // site. Card supplies the bg-surface + border-subtle +
          // rounded-2xl + p-5 shape — the previous inline-style
          // background + border declarations are now Card-supplied.
          <Card asChild variant="surface" padding="md">
            <section role="status" aria-live="polite">
              <p
                className="text-sm font-semibold"
                style={{ color: 'var(--fg-primary)' }}
              >
                Check your email.
              </p>
              <p
                className="mt-2 text-sm"
                style={{ color: 'var(--fg-tertiary)' }}
              >
                If an account exists for that email, a password-reset link is
                on its way. The link is valid for 60 minutes.
              </p>
              <a
                href="/login"
                className="mt-4 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
                style={{ color: 'var(--fg-brand)' }}
              >
                ← Back to sign in
              </a>
            </section>
          </Card>
        ) : (
          <form onSubmit={onSubmit} noValidate aria-busy={submitting}>
            {/* SCRUM-100: email field composes the kit TextField primitive.
                Kit handles label↔input wiring, aria-invalid, aria-describedby,
                error paragraph + role="alert", and the bg-surface +
                border-default + rounded-xl + min-h-touch shape. */}
            <TextField
              label="Work email"
              type="text"
              autoComplete="username"
              inputMode="email"
              spellCheck={false}
              autoCapitalize="off"
              placeholder="you@company.com"
              disabled={submitting}
              error={errors.email?.message}
              {...register('email')}
            />

            {/* Submit-time error region (separate from per-field aria-describedby) */}
            <div
              role="alert"
              aria-live="polite"
              className="mb-3 min-h-5"
            >
              {submitState.kind === 'error' ? (
                <p className="text-sm" style={{ color: '#c0392b' }}>
                  {submitState.message}
                </p>
              ) : null}
            </div>

            {/* SCRUM-101: composes the kit Button (primary, lg, full).
                The previous inline-style block (background/color +
                boxShadow) is now kit-managed via --bg-brand. */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              full
              disabled={submitting}
              aria-busy={submitting}
              aria-disabled={submitting}
              aria-label={
                submitting ? 'Sending reset link' : 'Send reset link'
              }
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </Button>

            <a
              href="/login"
              className="mt-4 inline-flex min-h-touch items-center justify-center text-[13px] font-semibold no-underline"
              style={{ color: 'var(--fg-brand)' }}
            >
              ← Back to sign in
            </a>
          </form>
        )}
      </div>
    </main>
  );
}
